<script lang="ts">
    import {onMount} from "svelte";

    interface ImageAccordionItem {
        title: string,
        content: string,
        image: string,
        video: string,
    }

    export let reverse: boolean = false;
    export let title: string = '';
    export let items: ImageAccordionItem[] = [];
    export let duration = 8000;
    export let actions: HTMLElement;


    let visible = false;
    let observerEl: HTMLElement;
    let currentIndex = 0;
    let interval: ReturnType<typeof setInterval>;
    let slidePercentage = 0;
    let isPaused = false;

    const startTimer = () => {
        clearInterval(interval);
        slidePercentage = 0;
        let elapsed = 0;
        const stepTime = 100;

        interval = setInterval(() => {
            if (isPaused) return;

            elapsed += stepTime;
            slidePercentage = Math.min(100, (elapsed / duration) * 100);

            if (elapsed >= duration) {
                nextSlide();
            }
        }, stepTime);
    }

    const setSlide = (index: number) => {
        currentIndex = index;
        startTimer();
    }

    const nextSlide = () => {
        currentIndex = (currentIndex + 1) % items.length;
        startTimer();
    }

    const pauseTimer = () => {
        isPaused = true;
    }

    const resumeTimer = () => {
        isPaused = false;
    }


    onMount(() => {
        if (actions && observerEl) {
            const target = observerEl.querySelector('[data-slot="actions"]');
            if (target) {
                while (actions.firstChild) {
                    target.appendChild(actions.firstChild);
                }
            }
        }
    });

    onMount(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                visible = entry.isIntersecting;
            },
            {threshold: 0.5}
        );

        if (observerEl) {
            observer.observe(observerEl);
        }

        return () => {
            observer.disconnect();
        };
    });


    $: currentItem = items[currentIndex];

    $: if (items.length > 0) {
        startTimer();
    }
</script>


<section bind:this={observerEl}>
	<div class="tac:max-w-[1600px] tac:mx-auto tac:px-4 tac:md:px-12">
		<div class="tac:lg:flex {reverse ?'tac:flex-row-reverse tac:gap-26' : 'tac:gap-20'}">
			<div class="tac:lg:w-2/5 tac:py-10">
				{#if title }
					<h2 class="bigger tac:md:pl-20"><strong>{title}</strong></h2>
				{/if}
				<div class="tac:lg:py-20 tac:mb-4">
					{#each items as item, index}
						<div
								class={`tac:group tac:border-solid tac:md:px-8 tac:cursor-pointer tac:border-0 tac:border-[red] tac:p-1 tac:relative tac:py-8 tac:md:pl-20`}
								on:mouseover={pauseTimer} on:focus={pauseTimer}
								on:mouseout={resumeTimer} on:blur={resumeTimer}
								on:click={() => setSlide(index)}
								role="none">

							<h3
									class="tac:font-bold tac:group-hover:pl-4 tac:transition-all tac:duration-[400ms]" style="margin-top:0">
								<strong>{item.title}</strong>
							</h3>

							<div class="{index === currentIndex ? 'tac:md:visible' : 'tac:md:hidden'}">
								<div class="tac:mb-4">{@html item.content}</div>
								<div class="tac:md:hidden">
									<img
											alt=""
											class="tac:w-full"
											src="{item.image}"
									/>
								</div>
							</div>

							<div class="tac:invisible tac:md:visible tac:absolute tac:top-0 tac:bottom-0 tac:left-0 tac:block tac:w-[3px] {index === currentIndex ? 'tac:bg-[#555]' : 'tac:bg-[#C4C3C5]'}">
								{#if index === currentIndex}
									<div class="tac:block tac:w-full tac:bg-[#FE3B6B] tac:transition-all tac:duration-[100ms]" style:height={slidePercentage + '%'}>
									</div>
								{/if}
							</div>
						</div>
					{/each}
				</div>
				<div class="tac:lg:pl-20 tac:mb-12" data-slot="actions">

				</div>
			</div>
			<div class="tac:lg:w-3/5 tac:pt-10 tac:justify-end tac:flex tac:hidden tac:md:flex tac:md:flex-col"
					role="none"
					on:mouseover={pauseTimer} on:focus={pauseTimer}
					on:mouseout={resumeTimer} on:blur={resumeTimer}>

				{#if currentItem.video }
					<!-- svelte-ignore a11y-media-has-caption -->
					<video
							autoplay
							muted playsinline
							controls
							class="tac:w-full tac:mb-[-30px] tac:rounded-2xl tac:aspect-[16/10] tac:object-cover tac:object-top tac:shadow-md tac:border-1 tac:border-solid tac:border-[#979797]"
					>
						<source src="{currentItem.video}" type="video/mp4"/>
					</video>
				{/if}
				{#if currentItem.image }
					<img
							alt=""
							class="tac:w-full tac:mb-[-30px] tac:rounded-2xl tac:aspect-[16/10] tac:object-cover tac:object-top tac:shadow-md tac:border-1 tac:border-solid tac:border-[#979797]"
							src="{currentItem.image}"
					/>
				{/if}

			</div>
		</div>
	</div>
</section>